export const measureDownloadSpeed = async ({
  minimumMbps = 2,
  sampleWindowMs = 1000,      // polling download speed every second by default.
  maxSlowWindows = 2,         // how many slow windows before abort
  timeoutMs = 6000,           // hard stop after 6 seconds to account for slower download speeds
} = {}) => {
  const fileUrl = "/download-speed-test-file.bin";

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let bytesDownloaded = 0;
  let slowWindows = 0;

  let firstByteTime: number | null = null;
  let lastSampleTime: number | null = null;
  let bytesAtLastSample = 0;

  try {
    const response = await fetch(fileUrl, {
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.body) {
      throw new Error("ReadableStream not supported");
    }

    const reader = response.body.getReader();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      bytesDownloaded += value.byteLength;

      const now = performance.now();

      // First byte: establish sampling baseline (ignore browser warm-up)
      if (!firstByteTime) {
        firstByteTime = now;
        lastSampleTime = now;
        bytesAtLastSample = bytesDownloaded;
        continue;
      }

      const elapsed = now - (lastSampleTime ?? now);

      if (elapsed >= sampleWindowMs) {
        const bytesDelta = bytesDownloaded - bytesAtLastSample;
        const seconds = elapsed / 1000;

        const mbps =
          (bytesDelta * 8) / seconds / 1_000_000;

        if (mbps < minimumMbps) {
          slowWindows++;
          if (slowWindows >= maxSlowWindows) {
            controller.abort();
            return {
              status: "slow",
              reason: "sustained-low-throughput",
              mbps,
              bytesDownloaded,
            };
          }
        } else {
          slowWindows = 0; // reset on recovery
        }

        lastSampleTime = now;
        bytesAtLastSample = bytesDownloaded;
      }
    }

    // Completed download — compute average AFTER first byte
    if (!firstByteTime) {
      return {
        status: "slow",
        reason: "no-data-received",
        mbps: 0,
        bytesDownloaded,
      };
    }

    const totalSeconds =
      (performance.now() - firstByteTime) / 1000;

    const avgMbps =
      (bytesDownloaded * 8) / totalSeconds / 1_000_000;

    return {
      status: "ok",
      reason: "",
      mbps: avgMbps,
      bytesDownloaded,
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return {
        status: "slow",
        reason: "timeout-or-abort",
        mbps: 0,
        bytesDownloaded,
      };
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};